/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";

import getSchema from "../../lib/api/getSchema";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import notificationState from "../../states/notificationState";
import CreateRecordButton from "../input/CreateRecordButton";
import UpdateRecordButton from "../input/UpdateRecordButton";
import DeleteRecordButton from "../input/DeleteRecordButton";
import { debounce } from "lodash";

/**
 * @COMPONENT_NAME MachineType
 * @DESCRIPTION 설비 유형 정보를 관리하는 화면 컴포넌트
 *
 * @PROPS
 *    - 이 컴포넌트는 직접적인 props를 받지 않습니다.
 *
 * @PASSTHROUGH_PROPS
 *    - {string} id - BasicCrudScreenType에 전달되는 테이블 식별자
 *    - {string} table - BasicCrudScreenType에 전달되는 테이블 이름
 *    - {string} view - BasicCrudScreenType에 전달되는 뷰 이름
 *    - {Function} onApiInitialized - API 초기화 시 호출되는 콜백
 *    - {Function} onInitialized - 그리드 초기화 시 호출되는 콜백
 *    - {Function} onAppendButtonInFrontOfSearch - 검색 영역 앞에 버튼 추가 함수
 *
 * @CALLBACKS
 *    - {Function} onSuccess - 작업 성공 시 알림을 표시하는 함수
 *    - {Function} onFailure - 작업 실패 시 오류 알림을 표시하는 함수
 *    - {Function} onNotifyWarning - 경고 알림을 표시하는 함수
 */

const MachineType = () => {
    const table = "MachineType";
    const view = "VwMachineType";

    const [schema, setSchema] = useState();
    const [api, setApi] = useState();
    const [grid, setGrid] = useState();
    const [machineTypeSelect, setMachineTypeSelect] = useState([]);

    const setNotification = useSetRecoilState(notificationState);

    useEffect(() => {
        let mounted = true;

        async function schemaSetting() {
            let schema = await getSchema(table);
            if (mounted) {
                setSchema(schema);
            }
        }

        schemaSetting();

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (grid) {
            grid.selectionChanged.addHandler(
                debounce((grid, event) => {
                    if (grid.selectedItems && grid.selectedItems[0]) {
                        setMachineTypeSelect(grid.selectedItems[0]);
                    } else {
                        setMachineTypeSelect([]);
                    }
                }, 10)
            );

            grid.itemsSourceChanged.addHandler(
                debounce((grid, event) => {
                    if (grid.selectedItems && grid.selectedItems[0]) {
                        setMachineTypeSelect(grid.selectedItems[0]);
                    } else {
                        setMachineTypeSelect([]);
                    }
                }, 10)
            );
        }
    }, [grid]);

    const onApiInitialized = (api) => {
        setApi(api);
    };

    const onInitialized = (grid) => {
        setGrid(grid);
    };
    const onSuccess = (msg) => {
        notifySuccess(msg);
    };

    const notifySuccess = (msg) => {
        setNotification({
            open: true,
            severity: "success",
            msg: msg,
        });
    };

    const onFailure = (reason) => {
        notifyFailure(reason.response ? reason.response.data : reason);
    };

    const notifyFailure = (msg) => {
        setNotification({
            open: true,
            severity: "error",
            msg: msg,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            open: true,
            severity: "warning",
            msg: msg,
        });
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
    };

    return (
        <BasicCrudScreenType
            id={table}
            table={table}
            view={view}
            onApiInitialized={onApiInitialized}
            onInitialized={onInitialized}
            noCreate
            noEdit
            noDelete
            noExcel
            onAppendButtonInFrontOfSearch={() => (
                <>
                    <BasicCrudScreenActionItemLayout>
                        <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                            <CreateRecordButton id={table} schema={schema} refresh={api} onSuccess={onSuccess} />
                        </AccessControl>
                    </BasicCrudScreenActionItemLayout>

                    <BasicCrudScreenActionItemLayout>
                        <AccessControl
                            accessTarget={schema && schema.name}
                            accessMethod={schema && (schema.updateWithPatch ? "PATCH" : "PUT")}
                        >
                            <UpdateRecordButton
                                id={table}
                                schema={schema}
                                grid={grid}
                                refresh={api}
                                disabled={machineTypeSelect?.length === 0 ? true : false}
                                onSuccess={notifySuccess}
                            />
                        </AccessControl>
                    </BasicCrudScreenActionItemLayout>

                    <BasicCrudScreenActionItemLayout>
                        <AccessControl accessTarget={schema && schema.name} accessMethod="DELETE">
                            <DeleteRecordButton
                                id={table}
                                schema={schema}
                                grid={grid}
                                refresh={api}
                                disabled={machineTypeSelect?.length === 0 ? true : false}
                                color="secondary"
                                onSuccess={onSuccess}
                                onFailure={onFailure}
                                onPartialFailure={onNotifyWarning}
                            />
                        </AccessControl>
                    </BasicCrudScreenActionItemLayout>
                </>
            )}
        />
    );
};

export default MachineType;
